import React, { useEffect, useRef, useState } from 'react'
import styles from './EnterToSystemSection.module.scss'
import { Button } from 'components'
import userInfo from 'utils/userInfo'
import { useNavigate } from 'react-router-dom'
import windows from "assets/icons/windows.svg";

import { getSlides } from 'service/CatalogService';
import frame from "assets/img/frame.png";
import frame1 from "assets/img/frame1.png";
import frame2 from "assets/img/frame2.png";
import frame3 from "assets/img/frame3.png";
import frame4 from "assets/img/frame4.png";

import frame_mobile from "assets/img/frame_mobile.png";
import frame_mobile1 from "assets/img/frame_mobile1.png";
import frame_mobile2 from "assets/img/frame_mobile2.png";
import frame_mobile3 from "assets/img/frame_mobile3.png";
import frame_mobile4 from "assets/img/frame_mobile4.png";

const EnterToSystemSection = () => {
  const user = userInfo()
  const navigate = useNavigate()

  const defaultFrames = [frame, frame1, frame2, frame3, frame4]
  const defaultFramesMobile = [frame_mobile, frame_mobile1, frame_mobile2, frame_mobile3, frame_mobile4]

  // slidesData: array of { id, image, image_mobile }
  const [slidesData, setSlidesData] = useState(() =>
    defaultFrames.map((img, idx) => ({ id: `local-${idx}`, image: img, image_mobile: defaultFramesMobile[idx] || img }))
  )

  const [active, setActive] = useState(0)
  const intervalRef = useRef(null)
  const isPausedRef = useRef(false)
  const pointerDownRef = useRef(false)
  const startXRef = useRef(0)
  const currentXRef = useRef(0)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  // Fetch remote slides and replace defaults if available
  useEffect(() => {
    let mounted = true
    const tryFetch = async () => {
      try {
        const res = await getSlides()
        if (!mounted || !res) return
        // expect res to be array of { id, image, image_mobile }
        if (Array.isArray(res) && res.length) {
          const mapped = res.map((s) => ({ id: s.id, image: s.image, image_mobile: s.image_mobile || s.image }))
          setSlidesData(mapped)
          // reset active to 0 to avoid out-of-bounds
          setActive(0)
        }
      } catch (e) {
        // keep defaults on error
        // console.warn('Failed to load slides', e)
      }
    }
    tryFetch()
    return () => {
      mounted = false
    }
  }, [])

  // autoplay effect depends on slidesData length
  useEffect(() => {
    const start = () => {
      if (intervalRef.current) return
      intervalRef.current = setInterval(() => {
        if (!isPausedRef.current && slidesData.length > 0) setActive((s) => (s + 1) % slidesData.length)
      }, 3000)
    }

    // start autoplay only if we have more than 1 slide
    if (slidesData.length > 1) start()

    return () => clearInterval(intervalRef.current)
  }, [slidesData.length])

  const restartInterval = () => {
    if (intervalRef.current || slidesData.length <= 1) return
    intervalRef.current = setInterval(() => {
      if (!isPausedRef.current && slidesData.length > 0) setActive((s) => (s + 1) % slidesData.length)
    }, 3000)
  }

  const nextSlide = () => {
    setActive((s) => (s + 1) % Math.max(1, slidesData.length))
  }

  const prevSlide = () => {
    setActive((s) => (s - 1 + slidesData.length) % Math.max(1, slidesData.length))
  }

  const goTo = (index) => {
    // protect against out-of-bounds
    const idx = Math.max(0, Math.min(index, slidesData.length - 1))
    setActive(idx)
    // stop autoplay after manual change
    isPausedRef.current = true
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  // pointer / touch handlers for drag/swipe
  const onPointerDown = (e) => {
    // support touch and mouse
    // If this is a pointer event from mouse, only start on left button (button === 0)
    if (e.pointerType === 'mouse' && typeof e.button === 'number' && e.button !== 0) return
    // prevent native image drag for mouse
    if (e.pointerType === 'mouse' && typeof e.preventDefault === 'function') e.preventDefault()
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0
    pointerDownRef.current = true
    startXRef.current = clientX
    currentXRef.current = clientX
    isDraggingRef.current = false
    setIsDragging(true)
    // pause autoplay while dragging
    isPausedRef.current = true
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  const onPointerMove = (e) => {
    if (!pointerDownRef.current) return
    // prevent native drag behavior for mouse moves
    if (e.pointerType === 'mouse' && typeof e.preventDefault === 'function') e.preventDefault()
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0
    currentXRef.current = clientX
    const delta = clientX - startXRef.current
    const absDelta = Math.abs(delta)
    const threshold = 60
    // mark as dragging after small movement
    if (!isDraggingRef.current && absDelta > 8) {
      isDraggingRef.current = true
    }
    // if moved enough, trigger slide change and reset startX to allow multiple swipes in one drag
    if (absDelta >= threshold) {
      if (delta < 0) {
        nextSlide()
      } else {
        prevSlide()
      }
      // reset start point to allow subsequent swipes while holding
      startXRef.current = clientX
    }
  }

  const onPointerUp = () => {
    if (!pointerDownRef.current) return
    pointerDownRef.current = false
    isDraggingRef.current = false
    setIsDragging(false)
    // resume autoplay after interaction
    isPausedRef.current = false
    restartInterval()
  }

  const handleMouseEnter = () => {
    isPausedRef.current = true
  }

  const handleMouseLeave = () => {
    isPausedRef.current = false
    // restart interval
    if (!intervalRef.current && slidesData.length > 1) {
      intervalRef.current = setInterval(() => {
        if (!isPausedRef.current) setActive((s) => (s + 1) % slidesData.length)
      }, 3000)
    }
  }

  return (
    <div className={styles.section} id="enter">
      <div className={styles.left_side}>
        <h1>Твой мир, твои правила: мерч, одежда, уникальные товары и донаты</h1>

        <div
          className={`${styles.slider} ${isDragging ? styles.dragging : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        >
          <div className={styles.slides}>
            {/* measurer keeps layout size according to your px/adaptive rules */}
            <picture className={styles.measurerPicture}>
              <source
                srcSet={slidesData[active]?.image_mobile || slidesData[active]?.image || ''}
                media="(max-width: 767px)"
              />
              <img src={slidesData[active]?.image || ''} alt="" className={styles.measurer} />
            </picture>
            {slidesData.map((slide, idx) => (
              <picture key={slide.id || idx} className={`${styles.slide} ${idx === active ? styles.active : ''}`}>
                <source srcSet={slide.image_mobile || slide.image} media="(max-width: 767px)" />
                <img src={slide.image} alt={`slide-${idx + 1}`} className={styles.icon} />
              </picture>
            ))}
          </div>

        </div>

      </div>

        <div className={styles.dots}>
          {slidesData.map((_, idx) => (
            <button
              key={idx}
              className={`${styles.dot} ${idx === active ? styles.activeDot : ''}`}
              onClick={() => goTo(idx)}
              aria-label={`Перейти к слайду ${idx + 1}`}
            />
          ))}
        </div>
    </div>
  )
}

export default EnterToSystemSection
